import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useCreateAdmin, useUpdateAdmin } from '../../hooks/useSuperAdminAdmins';
import LocationPicker from '@/features/locations/components/LocationPicker';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  mobile_phone: '',
  national_id: '',
  email: '',
  address: '',
  postal_code: '',
  location_id: null,
  latitude: null,
  longitude: null,
  password: '',
  vendor_local_phone: '', 
  vendor_shop_name_fa: '', 
  vendor_shop_name_en: '',
  shaba_number: '', 
  commission_rate: '0.00',
};

export default function AdminCreateModal({ mode, admin, onClose }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(isEdit ? { ...EMPTY_FORM, ...admin, password: '' } : EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState('');

  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();
  const isSaving = createAdmin.isPending || updateAdmin.isPending;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const buildPayload = () => {
    const excluded = isEdit ? ['mobile_phone', 'national_id'] : [];
    return Object.fromEntries(
      Object.entries(form).filter(([key, val]) => {
        if (excluded.includes(key)) return false;
        if (isEdit && key === 'password' && val === '') return false;
        return val !== '';
      })
    );
  };

  const extractErrorMessage = () => {
    return 'انجام عملیات ناموفق بود. اطلاعات واردشده را بررسی کنید و دوباره تلاش کنید.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    const payload = buildPayload();
    try {
      if (isEdit) {
        await updateAdmin.mutateAsync({ adminId: admin.id, adminData: payload });
      } else {
        await createAdmin.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" dir="rtl">
        <h2 className="text-lg font-semibold mb-4">{isEdit ? 'ویرایش ادمین' : 'ایجاد ادمین ایرانچارسوق'}</h2>

        {errorMessage && <div className="mb-4 p-2 bg-red-50 text-red-700 text-sm rounded">{errorMessage}</div>}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="نام" name="first_name" value={form.first_name} onChange={handleChange} required />
            <Field label="نام خانوادگی" name="last_name" value={form.last_name} onChange={handleChange} required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="شماره موبایل" name="mobile_phone" value={form.mobile_phone} onChange={handleChange} required={!isEdit} disabled={isEdit} placeholder="۹۱۲۳۴۵۶۷۸۹" />
            <Field label="کد ملی" name="national_id" value={form.national_id} onChange={handleChange} required={!isEdit} disabled={isEdit} />
          </div>

          <Field label="ایمیل" name="email" type="email" value={form.email} onChange={handleChange} />

          <div className="grid grid-cols-2 gap-3">
            <Field label="آدرس" name="address" value={form.address} onChange={handleChange} />
            <Field label="کد پستی" name="postal_code" value={form.postal_code} onChange={handleChange} />
          </div>

          <LocationPicker
            idPrefix="admin"
            value={{
              location_id: form.location_id,
              latitude: form.latitude,
              longitude: form.longitude,
              address: form.address,
            }}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          />

          {!isEdit && (
            <Field
              label="رمز عبور"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
            />
          )}

          <fieldset className="border rounded p-3 mt-2">
            <legend className="text-sm font-medium px-1">اطلاعات شعبه ایرانچارسوق</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="شماره موبایل" name="vendor_local_phone" value={form.vendor_local_phone} onChange={handleChange} />
              <Field label="شماره شبا" name="shaba_number" value={form.shaba_number} onChange={handleChange} />
              <Field label="نام شعبه (فارسی)" name="vendor_shop_name_fa" value={form.vendor_shop_name_fa} onChange={handleChange} />
              <Field label="نام لاتین شعبه برای جست‌وجو" name="vendor_shop_name_en" value={form.vendor_shop_name_en} onChange={handleChange} />
              <Field label="نرخ کمیسیون (%)" name="commission_rate" type="number" step="0.01" value={form.commission_rate} onChange={handleChange} />
            </div>
          </fieldset>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded inline-flex items-center gap-2">
              <X size={16} aria-hidden="true" />
              <span>انصراف</span>
            </button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2">
              <Plus size={16} aria-hidden="true" />
              <span>{isSaving ? 'در حال ذخیره...' : isEdit ? 'ذخیره تغییرات' : 'ایجاد ادمین'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, type = 'text', value, onChange, required, disabled, placeholder, step }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-700">
        {label}
        {required && <span aria-hidden="true" className="text-red-600"> *</span>}
      </span>
      <input
        type={type} name={name} value={value ?? ''} onChange={onChange}
        required={required} disabled={disabled} placeholder={placeholder} step={step}
        className="mt-1 w-full border rounded px-2 py-1 disabled:bg-gray-100"
      />
    </label>
  );
}